import _ from "lodash";
import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  DropdownItemProps,
  Form,
  FormProps,
  Input,
  Label,
  Message,
  Modal,
  Segment,
  Select,
  Table,
} from "semantic-ui-react";
import {
  ModalControlData,
  useModalControl,
  useRequest,
} from "../hooks/commonHooks";
import { useFormConnect, useFormData } from "../hooks/formHooks";
import { restRequest } from "../http";
import { AREAS } from "../models/common_model";
import { PageData } from "../models/common_model";
import { UserData } from "../models/user_model";
import { fieldError, formatDateTime } from "../utils";
import { Page } from "./commons/Page";
import { dialogControl } from "../controls/dialog-control";
import { notificationControl } from "../controls/notification-control";
const downloadCsv = require("download-csv") as any;

const availableFieldNames = [
  "pass_all",
  "pass_any",
  "last_status",
  "last_measured_at",
  "RMA_root_cause",
  "refurbishment_serial_number"
];

interface CSVData {
  headers: string[];
  data: string[][];
}

export function ExportCsvBySerials(props: {}) {
  const [searchData, setSearchField] = useFormData<{
    product: string;
    serial_numbers: string;
  }>({ product: "", serial_numbers: "" });

  const [csvData, setCSVData] = useState<CSVData>();

  const [productOptions, setProductOptions] = useState<DropdownItemProps[]>([]);
  const [stationOptions, setStationOptions] = useState<DropdownItemProps[]>([]);
  const [orderOptions, setOrderOptions] = useState<DropdownItemProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [snCount, setSNCount] = useState(0);
  const [columns, setColumns] = useState<
    { name: string; options: { operate?: string; value?: any } }[]
  >([]);
  const [orderAndStations, setOrderAndStations] = useState<{
    orders: string[];
    stations: string[];
  }>({ orders: [], stations:[] });

  const [allProducts, loadingAllProducts] = useRequest<string[]>(
    "testings/all_products",
    null,
    "get"
  );

  const [invalidErrors, setInvalidErrors] = useState<any>({});

  useEffect(() => {
    restRequest<{
        orders: string[];
        stations: string[];
      }>('testings/orders_and_stations', {product: searchData.product || '_none_'}, 'post').then(results => {

        setColumns(cs => cs.map(c => {
            if (["pass_all", "pass_any"].includes(c.name)) {
                if (!_.isEmpty(c.options.value)) {
                    return {...c, options: {...c.options, value: (c.options.value as string[]).filter(v => results.stations.includes(v))}}
                }
            }

            if (["last_status", "last_measured_at"].includes(c.name)) {
                if (!_.isEmpty(c.options.value)) {
                    return {...c, options: {...c.options, value: results.stations.includes(c.options.value) ? c.options.value : ''}}
                }
            }

            return c;
        }));
        setOrderAndStations(results);
      });

  }, [searchData.product]);

  useEffect(() => {
    if (allProducts) {
      const options = [
        { key: "", text: "All", value: "" },
        ...allProducts.map((p) => ({ key: p, text: p, value: p })),
      ];
      setProductOptions(options);
    }
  }, [allProducts]);

  useEffect(() => {
    if (orderAndStations) {
      setOrderOptions(
        orderAndStations.orders.map((o) => ({ key: o, text: o, value: o }))
      );
      setStationOptions(
        orderAndStations.stations.map((s) => ({ key: s, text: s, value: s }))
      );
    }
  }, [orderAndStations]);

  const onSubmit = (
    event: React.FormEvent<HTMLFormElement>,
    formProps: FormProps
  ) => {
    event.preventDefault();

    setLoading(true);

    setInvalidErrors({});

    restRequest<CSVData>(
      `testings/fetch_data_by_serial_numbers`,
      { ...searchData, columns },
      "post"
    )
      .then(
        (result) => {
          setCSVData(result);
        },
        (resp) => {
          if (resp.status === 2) {
            setInvalidErrors(resp.data);
          }
        }
      )
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Segment.Group>
        <Segment attached="top">
          <Form
            onSubmit={onSubmit}
            loading={loading}
            error={!_.isEmpty(invalidErrors)}
          >
            <Form.Group widths="equal">
              <Form.Input
                required
                inline
                label="Product"
                style={{ minWidth: "10rem" }}
                loading={loadingAllProducts}
                error={fieldError("product", invalidErrors)}
              >
                <Dropdown
                  name="product"
                  value={searchData.product || ""}
                  onChange={setSearchField}
                  options={productOptions}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Input>
            </Form.Group>

            <Form.Group widths="equal">
              <Form.TextArea
                label={`Serial Numbers ${snCount > 0 ? `(${snCount})` : ""}`}
                value={searchData.serial_numbers}
                onChange={(e) => {
                  e.stopPropagation();

                  const text = e.target.value;
                  const allSerials: string[] = [];
                  text.split("\n").forEach((t) => {
                    t.trim()
                      .split(",")
                      .forEach((v) => {
                        const sn = v.trim();
                        if (sn) {
                          allSerials.push(sn);
                        }
                      });
                  });
                  setSNCount(allSerials.length);
                  let value = allSerials.join(", ");
                  if (value) {
                    value += "\r\n";
                  }
                  setSearchField(null, { name: "serial_numbers", value });
                }}
                rows={5}
                required
                error={fieldError("serial_numbers", invalidErrors)}
              ></Form.TextArea>
            </Form.Group>

            <h3>Columns to load</h3>
            <Form.Group>
              <Table
                size="small"
                striped
                selectable
                compact
                celled
                style={{ width: "100%" }}
              >
                <Table.Header>
                  <th>Column</th>
                  <th>Options</th>
                  <th>
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        setColumns((fs) => [...fs, { name: "", options: {} }]);
                      }}
                      size="mini"
                      icon="add"
                      color="blue"
                    ></Button>
                  </th>
                </Table.Header>
                <Table.Body>
                  {columns.map((field, index) => {
                    const columnError = invalidErrors.columns?.find(
                      (c: any) => c.index === index
                    );
                    return (
                      <>
                        {columnError && (
                          <tr key={`error_${index}`}>
                            <td colSpan={100}>
                              <Label size="small" color="red" pointing="below">
                                {columnError.message.map((m: string) => (
                                  <span>{m}&nbsp;&nbsp;</span>
                                ))}
                              </Label>
                            </td>
                          </tr>
                        )}
                        <tr key={index}>
                          <td>
                            <Select
                              options={availableFieldNames.map((f) => ({
                                key: f,
                                text: f.split("_").join(" "),
                                value: f,
                              }))}
                              value={field.name}
                              onChange={(e, d) => {
                                setColumns((fs) =>
                                  fs.map((f, i) =>
                                    i === index
                                      ? { name: d.value as string, options: {} }
                                      : f
                                  )
                                );
                              }}
                            ></Select>
                          </td>
                          <td>
                            {["pass_all", "pass_any"].includes(field.name) && (
                              <>
                                <Form.Input label="Stations:" inline>
                                  <Select
                                    search
                                    value={field.options.value || []}
                                    onChange={(e, d) => {
                                      e.preventDefault();
                                      setColumns((fs) =>
                                        fs.map((f, i) =>
                                          i === index
                                            ? {
                                                ...f,
                                                options: {
                                                  value: (
                                                    d.value as string[]
                                                  ).filter(
                                                    (tv) => !_.isEmpty(tv)
                                                  ),
                                                },
                                              }
                                            : f
                                        )
                                      );
                                    }}
                                    options={(
                                      orderAndStations?.stations || []
                                    ).map((s) => ({
                                      key: s,
                                      text: s,
                                      value: s,
                                    }))}
                                    multiple
                                  ></Select>
                                </Form.Input>
                              </>
                            )}

                            {["last_status", "last_measured_at"].includes(
                              field.name
                            ) && (
                              <>
                                <Form.Input label="Station:" inline>
                                  <Select
                                    search
                                    value={field.options.value || []}
                                    onChange={(e, d) => {
                                      e.preventDefault();
                                      setColumns((fs) =>
                                        fs.map((f, i) =>
                                          i === index
                                            ? {
                                                ...f,
                                                options: {
                                                  value: d.value as string,
                                                },
                                              }
                                            : f
                                        )
                                      );
                                    }}
                                    options={(
                                      orderAndStations?.stations || []
                                    ).map((s) => ({
                                      key: s,
                                      text: s,
                                      value: s,
                                    }))}
                                  ></Select>
                                </Form.Input>
                              </>
                            )}
                          </td>
                          <td>
                            <Button
                              onClick={(e) => {
                                e.preventDefault();
                                dialogControl.confirm(
                                  "Are your sure to delete it?",
                                  (agree) => {
                                    if (agree) {
                                      setColumns((fs) =>
                                        fs.filter((f, i) => i !== index)
                                      );
                                    }

                                    return Promise.resolve();
                                  }
                                );
                              }}
                              size="mini"
                              icon="delete"
                              color="red"
                            ></Button>
                            {index !== 0 && (
                              <Button
                                onClick={(e) => {
                                  e.preventDefault();
                                  setColumns((fs) => [
                                    ...fs.slice(0, index - 1),
                                    fs[index],
                                    fs[index - 1],
                                    ...fs.slice(index + 1),
                                  ]);
                                }}
                                size="mini"
                                icon="arrow up"
                              ></Button>
                            )}

                            {index !== columns.length - 1 && (
                              <Button
                                onClick={(e) => {
                                  e.preventDefault();
                                  setColumns((fs) => [
                                    ...fs.slice(0, index),
                                    fs[index + 1],
                                    fs[index],
                                    ...fs.slice(index + 2),
                                  ]);
                                }}
                                size="mini"
                                icon="arrow down"
                              ></Button>
                            )}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </Table.Body>
              </Table>
            </Form.Group>
            {invalidErrors.message && (
              <Message error>{invalidErrors.message}</Message>
            )}
            <div>
              <Button primary>Query</Button>

                &nbsp;&nbsp;&nbsp;&nbsp;
              <Button
                as="a"
                onClick={(e) => {
                  e.preventDefault();

                  if (csvData) {
                    const datas: { [key: string]: string }[] = [];
                    const columns: { [key: string]: string } = {};
                    csvData.headers.forEach((h, i) => {
                      columns[`h${i}`] = `"${h}"`;
                    });
  
                    csvData.data.forEach((row) => {
                      const data: { [key: string]: string } = {};
                      row.forEach((v, i) => {
                        data[`h${i}`] = v;
                      });
                      datas.push(data);
                    });
  
                    downloadCsv(
                      datas,
                      columns,
                      searchData.product + "_result.csv"
                    );
                  } else {
                    notificationControl.showInfo('Please query first');
                  }
                }}
              >
                Export
              </Button>
            </div>
          </Form>
        </Segment>
        <Segment attached="bottom">
          {csvData && <Table striped>
            <Table.Header>
              <Table.Row>
                {csvData?.headers.map((h, i) => (
                  <th key={i}>{h}</th>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {csvData?.data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((r, i) => (
                    <td key={i}>{r}</td>
                  ))}
                </tr>
              ))}
            </Table.Body>
          </Table>}
        </Segment>
      </Segment.Group>
    </>
  );
}
